import { createContext, useCallback, useContext, useRef } from 'react';
import { useUserActionMutation } from '../custom-hooks/useUserActionMutation';

interface UserActionTrackingContextValue {
  trackComponentEntry: (componentName: string) => void;
  trackComponentExit: (componentName: string) => void;
}

export const UserActionTrackingContext = createContext<UserActionTrackingContextValue | null>(null);

interface UserActionTrackingProviderProps {
  children: React.ReactNode;
  skillId: string;
  userId: string;
}

export const UserActionTrackingProvider = ({ children, skillId, userId }: UserActionTrackingProviderProps) => {
  const [commitMutation] = useUserActionMutation();

  // Store skillId and userId in refs to avoid recreating callbacks when they change
  const skillIdRef = useRef(skillId);
  const userIdRef = useRef(userId);

  // Update refs when props change
  skillIdRef.current = skillId;
  userIdRef.current = userId;

  // Deduplication tracking
  const recentSubmissionsRef = useRef<Map<string, number>>(new Map());
  const DEDUP_WINDOW_MS = 2000; // 2 seconds

  // ✅ Stable callback - doesn't change unless commitMutation changes
  const trackComponentEntry = useCallback(
    (componentName: string) => {
      const key = `${userIdRef.current}_${skillIdRef.current}_${componentName}`;
      const now = Date.now();
      const lastSubmission = recentSubmissionsRef.current.get(key);

      // Skip if submitted recently
      if (lastSubmission && now - lastSubmission < DEDUP_WINDOW_MS) {
        return;
      }

      // Mark as submitted
      recentSubmissionsRef.current.set(key, now);

      commitMutation({
        variables: {
          input: {
            userAction: {
              userId: `UserNode:${userIdRef.current}`,
              skillId: skillIdRef.current,
              actionTypeId: 'ActionTypeNode:7', // Component Entered
              timeStamp: new Date().toISOString(),
              description: `${componentName}`,
            },
          },
        },
        onError: error => {
          // On error, remove from recent submissions to allow retry
          recentSubmissionsRef.current.delete(key);
          console.error('Failed to track component entry:', componentName, error);
        },
      });
    },
    [commitMutation],
  );

  const trackComponentExit = useCallback(
    (componentName: string) => {
      const key = `${userIdRef.current}_${skillIdRef.current}_${componentName}`;
      const now = Date.now();
      const lastSubmission = recentSubmissionsRef.current.get(key);

      // Skip if submitted recently
      if (lastSubmission && now - lastSubmission < DEDUP_WINDOW_MS) {
        return;
      }

      // Mark as submitted
      recentSubmissionsRef.current.set(key, now);

      commitMutation({
        variables: {
          input: {
            userAction: {
              userId: `UserNode:${userIdRef.current}`,
              skillId: skillIdRef.current,
              actionTypeId: 'ActionTypeNode:8', // Component Left
              timeStamp: new Date().toISOString(),
              description: `${componentName}`,
            },
          },
        },
        onError: error => {
          // On error, remove from recent submissions to allow retry
          recentSubmissionsRef.current.delete(key);
          console.error('Failed to track component exit:', componentName, error);
        },
      });
    },
    [commitMutation],
  );

  return (
    <UserActionTrackingContext.Provider value={{ trackComponentEntry, trackComponentExit }}>
      {children}
    </UserActionTrackingContext.Provider>
  );
};

// Custom hook to safely consume the context
export const useUserActionTracking = (): UserActionTrackingContextValue => {
  const context = useContext(UserActionTrackingContext);

  if (!context) {
    throw new Error('useUserActionTracking must be used within a UserActionTrackingProvider');
  }

  return context;
};
